import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { Modal } from '../../../Modal';
import { setRecapVisible } from '../../../../lib/recap-preference';
import { Card } from '../../ui/Card';
import { IconButton } from '../../ui/IconButton';
import styles from './RecapBanner.module.css';

/**
 * The conversation's rolling recap, directly above the composer — what this conversation has been about,
 * for someone returning to a thread they last touched days ago.
 *
 * The × does NOT dismiss this one recap: it turns recaps off for the device. A recap is regenerated as the
 * subject moves, so a per-recap dismissal would just bring a new one back a few turns later, and a user who
 * does not want the line does not want the next one either. The setting is also in Settings, because a
 * control that can only be switched off is a one-way door.
 *
 * Built on the shared Card + IconButton rather than a hand-rolled surface and a bare element — the
 * card-spec / button-spec tests enforce that, and they caught the first version of this file.
 *
 * Clicking it opens the whole recap in the shared `Modal` (whose scrim already carries the DS blur), since
 * the line is clamped to two and the rest of it otherwise has nowhere to go. Mirrors native `RecapOverlay`.
 */
export function RecapBanner({ recap }: { recap: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="sunken" className={styles.banner}>
      {/* The whole line opens it; the × keeps its own meaning and stops the click reaching here. */}
      <button type="button" className={styles.text} onClick={() => setExpanded(true)} title={t('chat.recap.expand')}>
        <span role="note" aria-label={t('chat.recap.label')}>
          {recap}
        </span>
      </button>
      <IconButton size="small" variant="text" title={t('chat.recap.hide')} aria-label={t('chat.recap.hide')} onClick={() => setRecapVisible(false)}>
        <X size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </IconButton>
      <Modal
        open={expanded}
        onClose={() => setExpanded(false)}
        title={t('chat.recap.label')}
        size="minimum"
        maxHeightPx={650}
        scrollBody
      >
        <p className={styles.full}>{recap}</p>
      </Modal>
    </Card>
  );
}
